import {
  Button,
  Card,
  CardContent,
  Menu,
  MenuItem,
  Skeleton,
  Typography,
} from "@mui/material";
import { Box } from "@mui/system";
import { useSelector } from "react-redux";
import {
  ApplyForAllSurveys,
  ApplySurvey,
  GetCampaignSurveyTemplateById,
  GetTemplates,
  UpdateSurveyTemplateDefault,
  selectCampaignInfo,
  selectSelectedTemplateID,
  selectSurveyInfo,
  selectSurveyTemplateID,
  selectTemplates,
  setSelectedTemplateID
} from "store/slicers/campaignDetail";

import { useEffect, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useAsyncDispatch } from "shared/helpers/hooks/useAsyncDispatch";
import useTranslation from "shared/helpers/hooks/useTranslation";
import BasicAutocomplete from "shared/ui/Autocomplete";
import ButtonLoader from "shared/ui/ButtonLoader";
import RightDrawer from "shared/ui/Drawer";
import { ERequestStatus } from "store/enums/index.enum";
import { ITemplate } from "store/interfaces/campaignDetails";
import {
  selectCampaignLoading,
  setLoading
} from "store/slicers/common";
import { defaultFilterValues } from "resources/constants";
import AddEditTemplate from "./components/AddEditTemplate";
import UploadImage from "./components/UploadImage";

interface IInitialValues {
  template: ITemplate | null;
  image: string;
  selected_template: number | null;
}

const initialValues: IInitialValues = {
  template: null,
  image: "",
  selected_template: null,
};

const DesignTab = () => {
  const t = useTranslation();
  const surveyInfo = useSelector(selectSurveyInfo);
  const templateList = useSelector(selectTemplates);
  const dispatch = useAsyncDispatch();
  const methods = useForm<IInitialValues>({
    defaultValues: initialValues,
  });
  const { watch ,setValue,reset} = methods;
  const selectedTemplateID = useSelector(selectSelectedTemplateID);
  const campaignInfo = useSelector(selectCampaignInfo);
  const campaignLoadingState = useSelector(selectCampaignLoading);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [menuOpen, onMenuOpen] = useState(false);
  const [editTemplateData, setEditTemplateData] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(false);
  const templatesFetchedRef = useRef(false);
  const lastTemplateIdRef = useRef<number | null>(null);
  const surveyTemplateID = useSelector(selectSurveyTemplateID);
  const [allTemplatesList, setAllTemplatesList] = useState<ITemplate[]>([]);
  const [templateID, setTemplateId] = useState(null)
    
  const openEditTemplateDrawer = () => {
    setEditTemplateData(watch("template"));
    setDrawerOpen(true);
  };

  const handleSuccess = async () => {
    // update data after edit
    const { payload: updatedTemplate } = await dispatch(
      GetCampaignSurveyTemplateById(surveyInfo.details.id)
    );    
    setValue("template", updatedTemplate);
    setDrawerOpen(false);
    setEditTemplateData(null);
  };
  
  const handleUpdateSurveyDefault = async () => {
    const template = watch("template")
    if(!template) return;
    await dispatch(
      UpdateSurveyTemplateDefault({
        data: {
          logoImage: {
            base64Image: template?.logoImageBase64,
            extension: "",
            removeImage: false,
          },
          image: {
            base64Image: methods?.watch("image"),
            removeImage: false,
            extension: "",
          },
            name: template.name,
          questionColor: template?.questionColor,
          answerColor: template?.answerColor,
          buttonColor: template?.buttonColor,
          buttonTextColor: template?.buttonTextColor,
          publicTemplateID: Number(template?.id),
        },
        id: surveyTemplateID,
      })
    );
  };

  const handleApply = async () => {
    await dispatch(setLoading(true));
    await handleUpdateSurveyDefault();
    onMenuOpen(false);
    const { meta } = await dispatch(
      ApplySurvey({
        surveyID: String(surveyInfo.details.id),
        templateID: surveyTemplateID,
      })
    );

    if (meta.requestStatus !== ERequestStatus.FULFILLED) {
      await dispatch(setLoading(false));
      return;
    }

     await dispatch(
      GetCampaignSurveyTemplateById(surveyInfo.details.id)
    );

    await dispatch(setSelectedTemplateID(null));

    // Reset form to show the newly applied template
   
    
    await dispatch(setLoading(false));
    toast.success("Template applied successfully");
  };

  const handleApplyAll = async () => {
    await dispatch(setLoading(true));
    await handleUpdateSurveyDefault();

    onMenuOpen(false);
    const { meta } = await dispatch(
      ApplyForAllSurveys({
        campaignID: String(campaignInfo.id),
        templateID: surveyTemplateID,
      })
    );

    if (meta.requestStatus !== ERequestStatus.FULFILLED) {
      await dispatch(setLoading(false));
      return;
    }
    
    const { payload: updatedTemplate } = await dispatch(
      GetCampaignSurveyTemplateById(surveyInfo.details.id)
    );

    await dispatch(setSelectedTemplateID(null));
    // Reset form to show the newly applied template
    if (updatedTemplate?.id) {
      const appliedTemplate = templateList?.displayData?.find(
        (i) => i.id === updatedTemplate.id
      );
      if (appliedTemplate) {
        setValue("template", appliedTemplate);
        setValue("image", appliedTemplate.imageBase64 || "");
        setValue("selected_template", appliedTemplate.id);
      }
    }
    
    await dispatch(setLoading(false));
    toast.success("Template applied successfully");
  };

  const handleOpenMenu = (event) => {
    setAnchorEl(event.currentTarget);
    onMenuOpen(true);
  };

  const handleSelectTemplate = (val) => {
    setTemplateId(val?.id)
    dispatch(setSelectedTemplateID(val?.id));
    setValue("template", val);
    setValue("image", val?.imageBase64 || "");
  };

  const handleChangeImage = (val, name) => {
    setValue(name, val);
  };



  // Fetch templates only once
  useEffect(() => {
    if (campaignInfo?.id && !templatesFetchedRef.current) {
      const fetchTemplates = async () => {
        setIsLoadingTemplates(true);
        const { payload } = await dispatch(
          GetTemplates({ formData: {...defaultFilterValues, length:50}, id: campaignInfo.id })
        );
        setAllTemplatesList(payload?.displayData || []);
        setIsLoadingTemplates(false);
        templatesFetchedRef.current = true;
      };
      fetchTemplates();
    }
  }, [dispatch, campaignInfo?.id]);
  
  useEffect(() => {
    const currentTemplateId = surveyInfo?.template?.id;
    if (currentTemplateId && currentTemplateId !== lastTemplateIdRef.current) {
      reset({
        template: surveyInfo?.template,
        image: surveyInfo?.template?.imageBase64,
        selected_template: currentTemplateId,
      });
      lastTemplateIdRef.current = currentTemplateId;
    }
  }, [reset, surveyInfo?.template]);

 
  return (
    <FormProvider {...methods}>
      {campaignLoadingState ? (
        <Box p={2}>
          <Box mb={2}>
            <Skeleton
              variant="rounded"
              animation="wave"
              width={"100%"}
              height="59px"
            />
          </Box>
          <Card>
            <CardContent>
              <Skeleton
                variant="rounded"
                animation="wave"
                width={"100%"}
                height="145px"
              />
            </CardContent>
          </Card>
        </Box>
      ) : (
        <Box p={2}>
          <Box>
            <BasicAutocomplete
              options={[...(allTemplatesList || [])?.filter((i) => i.id)]}
              inputLabel={"Template"}
              name={"template"}
              optionLabel="name"
              onChangeCB={handleSelectTemplate}
              loading={isLoadingTemplates}
              defaultValue={
                (allTemplatesList?.find(
                  (i) => i.id === selectedTemplateID
                ) || null) as any
              }
              async={true}
              fetchFn={async (search: string) => {
                const { payload } = await dispatch(
                  GetTemplates({ formData: { ...defaultFilterValues, search }, id: campaignInfo.id })
                );
                setAllTemplatesList(payload?.displayData || []);
              }}
            />
          </Box>
          <Box py={2}>
            {watch("selected_template") ? (
              <ButtonLoader onClick={openEditTemplateDrawer} isLoading={false}>
                <Typography>{t("edit_template")}</Typography>
              </ButtonLoader>
            ) : (
              <></>
            )}
          </Box>
          <Card>
            <CardContent>
              <UploadImage
                name="image"
                title="Image"
                val={methods.watch("image")}
                onChange={handleChangeImage}
              />
              {methods.watch("image") ? (
                <Box mt={2} display="flex" justifyContent={"flex-end"}>
                  <Button onClick={handleOpenMenu} variant="outlined">
                    <Typography>Apply</Typography>
                  </Button>
                  <Menu
                    anchorEl={anchorEl}
                    open={menuOpen}
                    onClose={() => onMenuOpen(false)}
                  >
                    <MenuItem onClick={() => handleApply()}>
                      <Typography>For this page</Typography>
                    </MenuItem>
                    <MenuItem onClick={() => handleApplyAll()}>
                      <Typography>For all pages</Typography>
                    </MenuItem>
                  </Menu>
                </Box>
              ) : null}
            </CardContent>
          </Card>
        </Box>
      )}
      <RightDrawer
        width={400}
        open={drawerOpen}
        setOpen={setDrawerOpen}
        title={"Add theme"}
      >
        <AddEditTemplate
          onSuccess={handleSuccess}
          editData={editTemplateData}
        />
      </RightDrawer>
    </FormProvider>
  );
};

export default DesignTab;
